import { formatMoney } from '@betterment/core';
import { Box, Icon, ListRow, Text } from '@betterment/samba-react';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';

import { CheckingAccountRow } from './CheckingAccountRow';
import { LeadingIcon } from './LeadingIcon';

import MessageCard from '../../../InAppMessages/MessageCard';
import { StyledLinkRow, titleCss } from '../../../Shared/components-v2/styled-link-row/StyledLinkRow';
import { urlBuilder } from '../../../Shared/urlBuilder';
import { EnvelopesCardQuery } from '../graphql/query.envelopesCard.generated';

type Envelope = EnvelopesCardQuery['envelopes'][number];
type EnvelopeAccount = Envelope['accounts'][number];

interface AccountRowProps {
  account: EnvelopeAccount;
  hasRecurringTransaction: boolean;
}

export const AccountRow: React.FC<AccountRowProps> = ({ account, hasRecurringTransaction }) => {
  const { colorMode } = useColorMode();
  if ('remote' in account) return <CheckingAccountRow account={account} />;

  const balance = getEnvelopeAccountBalance(account);
  const name = getEnvelopeAccountName(account);

  if (!balance || !name) return null;

  return (
    <StyledLinkRow to={urlBuilder.appAccount.absolute({ accountId: account.id })} name={name}>
      <ListRow.LeadingAsset>
        <LeadingIcon url={account.icon[colorMode]} />
      </ListRow.LeadingAsset>
      <ListRow.LeadingContent>
        <Text color="text" css={titleCss}>
          {name}
        </Text>
        <Text color="text.weak" variant="text50">
          {getRowSubtext(account)}
        </Text>
      </ListRow.LeadingContent>
      <ListRow.TrailingContent>
        <Box css={{ display: 'flex', alignItems: 'center', height: '100%', gap: 'space.100' }}>
          {hasRecurringTransaction && <Icon name="ArrowCircle" color="icon" size="xsmall" />}
          <Text color="text">{formatMoney(balance)}</Text>
        </Box>
      </ListRow.TrailingContent>
      {shouldShowInAppMessage(account) && (
        <ListRow.BottomContent css={{ mt: 'space.0' }}>
          <MessageCard location="summaryAccountLevelBanner" goalId={account.legacyGoalId} />
        </ListRow.BottomContent>
      )}
    </StyledLinkRow>
  );
};

function getEnvelopeAccountBalance(account: EnvelopeAccount) {
  switch (account.__typename) {
    case 'CheckingAccount':
      return account.remote?.balance;
    case 'CashAccount':
    case 'DirectTradingAccount':
      return account.balance;
    case 'ManagedAccount':
      return account.balance;
    case 'Five29Account':
      return account.connection?.balance;
    case 'ExternalAccountEnvelopeAssociation':
      return account.account.balance;
    default:
      return null;
  }
}

export function getEnvelopeAccountName(account: EnvelopeAccount) {
  switch (account.__typename) {
    case 'CheckingAccount':
      return account.remote?.name || 'Checking';
    case 'CashAccount':
    case 'DirectTradingAccount':
    case 'ManagedAccount':
      return account.nameOverride || account.accountDescription;
    case 'Five29Account':
      return account.connection?.name || '529 Plan';
    case 'ExternalAccountEnvelopeAssociation':
      return account.account.name;
    default:
      return null;
  }
}

export function getRowSubtext(account: EnvelopeAccount) {
  switch (account.__typename) {
    case 'CashAccount':
      return `${account.apy.rate}% APY*`;
    case 'ManagedAccount':
      return account.portfolio?.configurationDescription;
    default:
      return undefined;
  }
}

function shouldShowInAppMessage(account: EnvelopeAccount) {
  return 'legacyGoalId' in account;
}
